import axios from "axios";
import {
  getAuthRole,
  clearAllAuthLocal,
  isAuthApiUrl,
  markAuthBounce,
} from "../utils/auth";

const api = axios.create({
  baseURL: `${process.env.REACT_APP_API_URL}/api`,
  headers: {
    "Content-Type": "application/json",
  },
  withCredentials: true, // ✅ Required for Django session cookies
});

let authRedirectInProgress = false;

// Function to get CSRF token from cookies
const getCsrfToken = () => {
  const name = 'csrftoken';
  let cookieValue = null;
  if (document.cookie && document.cookie !== '') {
    const cookies = document.cookie.split(';');
    for (let i = 0; i < cookies.length; i++) {
      const cookie = cookies[i].trim();
      if (cookie.substring(0, name.length + 1) === (name + '=')) {
        cookieValue = decodeURIComponent(cookie.substring(name.length + 1));
        break;
      }
    }
  }
  return cookieValue;
};

// Request interceptor - add CSRF token
api.interceptors.request.use(
  (config) => {
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }

    const csrfToken = getCsrfToken();
    if (csrfToken && ['post', 'put', 'patch', 'delete'].includes(config.method?.toLowerCase())) {
      config.headers['X-CSRFToken'] = csrfToken;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      const url = error.config?.url || "";
      const role = getAuthRole();
      if (role && !isAuthApiUrl(url) && !authRedirectInProgress) {
        authRedirectInProgress = true;
        const loginPath =
          role === "doctor"
            ? "/doctor/login"
            : role === "admin"
              ? "/admin/login"
              : "/user/login";
        const logoutUrl =
          role === "doctor"
            ? "doctor/logout/"
            : role === "admin"
              ? "admin/logout/"
              : "user/logout/";
        const reason = error.response?.data?.reason;
        const msg =
          reason === "session_replaced_or_expired"
            ? "You were logged out because this account signed in somewhere else."
            : null;
        if (msg) {
          window.dispatchEvent(
            new CustomEvent("fitnova:session-expired", { detail: { role, message: msg } })
          );
        }
        markAuthBounce();
        clearAllAuthLocal();

        const goLogin = () => {
          if (!window.location.pathname.startsWith(loginPath)) {
            window.location.assign(loginPath);
          } else {
            authRedirectInProgress = false;
          }
        };

        api.post(logoutUrl).catch(() => {}).finally(goLogin);
      }
    }

    return Promise.reject(error);
  }
);


export const sendMessage = async (message, sessionId = null) => {
  const payload = sessionId
    ? { message, session_id: sessionId }
    : { message };

  const res = await api.post("chat/", payload);
  return res.data;
};


export default api;
